import React, { useState, useEffect, useContext, useMemo, useCallback } from "react";
import * as Yup from "yup";
// eslint-disable-next-line no-unused-vars
import { Formik, Field } from "formik";
import { useToast } from "../../hooks/useToast";

import { makeStyles } from "@material-ui/core/styles";
import TextField from "@material-ui/core/TextField";
import Select from "@material-ui/core/Select";
import InputLabel from "@material-ui/core/InputLabel";
import MenuItem from "@material-ui/core/MenuItem";
import FormControl from "@material-ui/core/FormControl";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import {
  Grid,
  Paper,
  Tab,
  Tabs,
  Avatar,
  Box,
  IconButton,
  Tooltip,
  Typography,
  Button,
  Divider,
  Chip,
  Badge,
} from "@material-ui/core";
import EditIcon from "@material-ui/icons/Edit";
import PersonIcon from "@material-ui/icons/Person";
import CheckCircle from "@material-ui/icons/CheckCircle";
import Cancel from "@material-ui/icons/Cancel";
import GroupWork from "@material-ui/icons/GroupWork";
import Layers from "@material-ui/icons/Layers";
import Settings from "@material-ui/icons/Settings";
import Memory from "@material-ui/icons/Memory";
import ListAlt from "@material-ui/icons/ListAlt";
import Security from "@material-ui/icons/Security";
import Tune from "@material-ui/icons/Tune";
import Mail from "@material-ui/icons/Mail";
import Phone from "@material-ui/icons/Phone";
import VpnKey from "@material-ui/icons/VpnKey";
import Close from "@material-ui/icons/Close";
import DollarSign from "@material-ui/icons/AttachMoney";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import QueueSelect from "../QueueSelect";
import { AuthContext } from "../../context/Auth/AuthContext";
import useWhatsApps from "../../hooks/useWhatsApps";
import AvatarModal from "./AvatarModal";
import { getBackendUrl } from "../../config";

const backendUrl = getBackendUrl();
const profileLabelMap = {
  admin: "Administrador",
  user: "Usuário",
  motoqueiro: "Motoqueiro",
};

const sameModulePermissionOverrides = (left = {}, right = {}) => {
  const safeLeft = left || {};
  const safeRight = right || {};
  const leftEntries = Object.entries(safeLeft).sort(([leftKey], [rightKey]) =>
    leftKey.localeCompare(rightKey)
  );
  const rightEntries = Object.entries(safeRight).sort(([leftKey], [rightKey]) =>
    leftKey.localeCompare(rightKey)
  );

  return (
    leftEntries.length === rightEntries.length &&
    leftEntries.every(
      ([key, value], index) =>
        key === rightEntries[index][0] && value === rightEntries[index][1]
    )
  );
};

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexWrap: "wrap",
  },
  modernDialog: {
    "& .MuiDialog-paper": {
      borderRadius: 24,
      overflow: "hidden",
      boxShadow: "0 25px 60px rgba(0,0,0,0.3)",
      background:
        theme.palette.type === "dark"
          ? "#0f172a"
          : "#ffffff",
      width: "100%",
      [theme.breakpoints.up("sm")]: {
        minWidth: "600px",
      },
      [theme.breakpoints.up("md")]: {
        minWidth: "850px",
      },
      [theme.breakpoints.up("lg")]: {
        minWidth: "1100px",
      },
    }
  },
  modernModalHeader: {
    borderRadius: 18,
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    background:
      theme.palette.type === "dark"
        ? "linear-gradient(135deg, rgba(30,41,59,.95), rgba(15,23,42,.95))"
        : "linear-gradient(135deg, #eff6ff, #e0f2fe)",
    border: "1px solid rgba(148,163,184,.25)",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  tabContainer: {
    borderBottom: `1px solid ${theme.palette.type === "dark" ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.08)"}`,
    marginBottom: theme.spacing(2),
    backgroundColor: theme.palette.type === "dark" ? "rgba(15,23,42,0.6)" : "#f8fafc",
    borderRadius: 12,
    padding: "4px 8px",
  },
  tabItem: {
    textTransform: "none",
    fontWeight: 700,
    fontSize: "0.88rem",
    minHeight: 44,
    borderRadius: 10,
    marginRight: theme.spacing(1),
    transition: "all 0.2s ease",
    "&.Mui-selected": {
      color: theme.palette.type === "dark" ? "#60a5fa" : "#2563eb",
      backgroundColor: theme.palette.type === "dark" ? "rgba(59,130,246,0.15)" : "#ffffff",
      boxShadow: theme.palette.type === "dark" ? "none" : "0 2px 8px rgba(0,0,0,0.06)",
    }
  },
  tabContentContainer: {
    minHeight: 380,
    maxHeight: 520,
    overflowY: "auto",
    paddingRight: theme.spacing(0.5),
    paddingTop: theme.spacing(2),
    marginTop: theme.spacing(2),
    ...theme.scrollbarStyles,
  },
  sectionTitle: {
    fontWeight: 800,
    fontSize: "0.95rem",
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
    color: theme.palette.type === "dark" ? "#93c5fd" : "#1e40af",
  },
  permissionGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(230px, 1fr))",
    gap: theme.spacing(1.2),
    marginTop: theme.spacing(1),
  },
  permissionCard: {
    borderRadius: 14,
    border: `1px solid ${theme.palette.type === "dark" ? "rgba(255,255,255,0.08)" : "rgba(148,163,184,0.2)"}`,
    padding: theme.spacing(1.2),
    backgroundColor: theme.palette.type === "dark" ? "rgba(30,41,59,0.4)" : "#ffffff",
    transition: "all 0.15s ease",
    "&:hover": {
      borderColor: "rgba(59,130,246,0.3)",
    }
  },
  permissionLabel: {
    fontSize: "0.82rem",
    fontWeight: 700,
    marginBottom: theme.spacing(0.8),
    color: theme.palette.type === "dark" ? "#e2e8f0" : "#334155",
  },
  quickActionHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: theme.spacing(1.5),
    paddingBottom: theme.spacing(1),
    borderBottom: `1px solid ${theme.palette.type === "dark" ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)"}`,
  },
  avatarContainer: {
    display: "flex",
    justifyContent: "center",
    position: "relative",
    "&:hover $avatarOverlay": {
      opacity: 1,
    },
  },
  avatar: {
    width: 58,
    height: 58,
    boxShadow: theme.shadows[3],
    border: `2px solid ${theme.palette.background.paper}`,
    borderRadius: "50%",
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  },
  avatarOverlay: {
    position: "absolute",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    borderRadius: "50%",
    opacity: 0,
    width: 58,
    height: 58,
    margin: "auto",
  },
  editButton: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    "&:hover": {
      backgroundColor: theme.palette.primary.dark,
    },
  },
  roundedInput: {
    "& .MuiOutlinedInput-root": {
      borderRadius: 10,
    },
  },
  roundedSelect: {
    "& .MuiOutlinedInput-root": {
      borderRadius: 10,
    },
  },
  emailSection: {
    background: theme.palette.background.default,
    borderRadius: 12,
    padding: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
  emailAccountCard: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 10,
    padding: theme.spacing(1.25),
    marginBottom: theme.spacing(1),
    background: theme.palette.background.paper,
  },
  emailCardHeader: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: theme.spacing(1),
  },
  emailActionsRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
}));

const UserSchema = Yup.object().shape({
  name: Yup.string().min(2, "Muito curto!").max(50, "Muito longo!").required("Obrigatório"),
  password: Yup.string().min(5, "Muito curto!").max(50, "Muito longo!"),
  email: Yup.string().email("E-mail inválido").required("Obrigatório"),
  voip: Yup.string().nullable(),
  voipUser: Yup.string().nullable(),
  voipPass: Yup.string().nullable(),
  allHistoric: Yup.string().nullable(),
  address: Yup.string().max(255, "Muito longo!"),
  contactMessage: Yup.string().max(255, "Muito longo!"),
  bio: Yup.string().max(2000, "Muito longo!"),
});

const COMPANY_MODULE_GROUPS = [
  {
    title: "Módulos Financeiros & Vendas",
    icon: <DollarSign fontSize="small" />,
    keys: [
      "allowAsaas",
      "allowFinanceiro",
      "allowWalletManagement",
      "allowMyWallet",
      "allowCashierPdv",
      "allowPdv",
      "allowComidaNaPorta",
      "allowBarber"
    ]
  },
  {
    title: "Campanhas, E-mail & Automações",
    icon: <Memory fontSize="small" />,
    keys: [
      "allowCampaigns",
      "allowCampaignsList",
      "allowEmailCampaigns",
      "allowContactLists",
      "allowEmail",
      "allowAutomations"
    ]
  },
  {
    title: "Ferramentas de Produtividade & Operação",
    icon: <Layers fontSize="small" />,
    keys: [
      "allowKanban",
      "allowTags",
      "allowInternalPortal",
      "allowTasks",
      "allowSchedules",
      "allowManageOtherUsersSchedules",
      "allowInternalChat",
      "allowConnections"
    ]
  },
  {
    title: "Inteligência Artificial & Integrações",
    icon: <GroupWork fontSize="small" />,
    keys: [
      "allowTalkIa",
      "allowAiAgents",
      "aiAdminAssistantAccess",
      "allowIntegrations",
      "allowKnowledgeBase",
      "allowApi"
    ]
  },
  {
    title: "Saúde, Beleza & Telefonia",
    icon: <Tune fontSize="small" />,
    keys: [
      "allowAgendaCare",
      "allowOdontoCare",
      "allowCallcenterUser",
      "allowCallcenterAdmin",
      "allowErpPhone"
    ]
  },
  {
    title: "Administração & Sistema",
    icon: <Security fontSize="small" />,
    keys: [
      "allowUsers",
      "allowUserProfile",
      "allowQueues",
      "allowSettings",
      "allowAnnouncements"
    ]
  }
];

const COMPANY_MODULE_FIELDS = [
  { key: "allowAsaas", label: "Painel Asaas" },
  { key: "allowFinanceiro", label: "Página do Financeiro" },
  { key: "allowWalletManagement", label: "Gerenciamento de Carteira" },
  { key: "allowMyWallet", label: "Minha Carteira" },
  { key: "allowCashierPdv", label: "Operar Caixa PDV" },
  { key: "allowPdv", label: "Módulo PDV" },
  { key: "allowComidaNaPorta", label: "Comida na Porta" },
  { key: "allowBarber", label: "Barbearia" },
  { key: "allowCampaigns", label: "Módulo de Campanhas" },
  { key: "allowCampaignsList", label: "Listagem de Campanhas" },
  { key: "allowEmailCampaigns", label: "Campanhas de E-mail" },
  { key: "allowContactLists", label: "Listas de Contatos" },
  { key: "allowEmail", label: "E-mail (IMAP/SMTP)" },
  { key: "allowKanban", label: "Painel Kanban" },
  { key: "allowTags", label: "Etiquetas / Tags" },
  { key: "allowInternalPortal", label: "Portal Interno" },
  { key: "allowTasks", label: "Gerenciador de Tarefas" },
  { key: "allowSchedules", label: "Agendamentos" },
  {
    key: "allowManageOtherUsersSchedules",
    label: "Gerenciar agenda de outros usuários",
  },
  { key: "allowInternalChat", label: "Chat Interno" },
  { key: "allowConnections", label: "Gestão de Conexões" },
  { key: "allowTalkIa", label: "Secretária & Talk.IA" },
  { key: "allowAiAgents", label: "Agentes de IA" },
  { key: "aiAdminAssistantAccess", label: "Acesso à IA Secretária", boolean: true },
  { key: "allowIntegrations", label: "Integrações de Filas" },
  { key: "allowKnowledgeBase", label: "Base de Conhecimento" },
  { key: "allowApi", label: "API Externa" },
  { key: "allowAgendaCare", label: "AgendaCare (Saúde)" },
  { key: "allowOdontoCare", label: "OdontoCare (Odonto)" },
  { key: "allowCallcenterUser", label: "Callcenter Operador" },
  { key: "allowCallcenterAdmin", label: "Central Callcenter" },
  { key: "allowErpPhone", label: "ERP-Phone / Telefonia" },
  { key: "allowUsers", label: "Gestão de Usuários" },
  { key: "allowUserProfile", label: "Perfil do Usuário" },
  { key: "allowQueues", label: "Gestão de Filas" },
  { key: "allowSettings", label: "Configurações Globais" },
  { key: "allowAutomations", label: "Automações & Gatilhos" },
  { key: "allowAnnouncements", label: "Informativos Internos" },
];

const PERMISSION_BULK_VALUES = {
  allTicket: { enabled: "enabled", disabled: "disabled" },
  allowGroup: { enabled: true, disabled: false },
  allowSendAudio: { enabled: "enabled", disabled: "disabled" },
  allowSendMedia: { enabled: "enabled", disabled: "disabled" },
  allHistoric: { enabled: "enabled", disabled: "disabled" },
  allowViewAllClosedTickets: { enabled: "enabled", disabled: "disabled" },
  allowContacts: { enabled: "enabled", disabled: "disabled" },
  allowAutomations: { enabled: "enabled", disabled: "disabled" },
  allowCampaigns: { enabled: "enabled", disabled: "disabled" },
  allowAgendaCare: { enabled: "enabled", disabled: "disabled" },
  allowOdontoCare: { enabled: "enabled", disabled: "disabled" },
  allowCallcenterUser: { enabled: "enabled", disabled: "disabled" },
  allowCallcenterAdmin: { enabled: "enabled", disabled: "disabled" },
  allowCashierPdv: { enabled: "enabled", disabled: "disabled" },
  allowPdv: { enabled: "enabled", disabled: "disabled" },
  allowErpPhone: { enabled: "enabled", disabled: "disabled" },
  allowViewAllSchedules: { enabled: "enabled", disabled: "disabled" },
  allowManageOtherUsersSchedules: {
    enabled: "enabled",
    disabled: "disabled",
  },
  allowLocation: { enabled: "enabled", disabled: "disabled" },
  allUserChat: { enabled: "enabled", disabled: "disabled" },
  spyTicket: { enabled: "enabled", disabled: "disabled" },
  userClosePendingTicket: { enabled: "enabled", disabled: "disabled" },
  allowConnections: { enabled: "enabled", disabled: "disabled" },
  showDashboard: { enabled: "enabled", disabled: "disabled" },
  allowRealTime: { enabled: "enabled", disabled: "disabled" },
  allowAttendance: { enabled: "enabled", disabled: "disabled" },
  allowEmail: { enabled: "enabled", disabled: "disabled" },
  allowKanban: { enabled: "enabled", disabled: "disabled" },
  allowTags: { enabled: "enabled", disabled: "disabled" },
  allowInternalPortal: { enabled: "enabled", disabled: "disabled" },
  allowTasks: { enabled: "enabled", disabled: "disabled" },
  allowMyWallet: { enabled: "enabled", disabled: "disabled" },
  allowSchedules: { enabled: "enabled", disabled: "disabled" },
  allowBarber: { enabled: "enabled", disabled: "disabled" },
  allowInternalChat: { enabled: "enabled", disabled: "disabled" },
  allowComidaNaPorta: { enabled: "enabled", disabled: "disabled" },
  allowCampaignsList: { enabled: "enabled", disabled: "disabled" },
  allowEmailCampaigns: { enabled: "enabled", disabled: "disabled" },
  allowContactLists: { enabled: "enabled", disabled: "disabled" },
  allowAnnouncements: { enabled: "enabled", disabled: "disabled" },
  allowApi: { enabled: "enabled", disabled: "disabled" },
  allowKnowledgeBase: { enabled: "enabled", disabled: "disabled" },
  allowUsers: { enabled: "enabled", disabled: "disabled" },
  allowUserProfile: { enabled: "enabled", disabled: "disabled" },
  allowQueues: { enabled: "enabled", disabled: "disabled" },
  allowTalkIa: { enabled: "enabled", disabled: "disabled" },
  allowAiAgents: { enabled: "enabled", disabled: "disabled" },
  allowIntegrations: { enabled: "enabled", disabled: "disabled" },
  allowAsaas: { enabled: "enabled", disabled: "disabled" },
  allowWalletManagement: { enabled: "enabled", disabled: "disabled" },
  allowFinanceiro: { enabled: "enabled", disabled: "disabled" },
  allowSettings: { enabled: "enabled", disabled: "disabled" },
};

const ATTENDANT_PERMISSION_PRESET = {
  allTicket: "disabled",
  allowGroup: false,
  allowSendAudio: "enabled",
  allowSendMedia: "enabled",
  allHistoric: "disabled",
  allowViewAllClosedTickets: "disabled",
  allowContacts: "enabled",
  allowAutomations: "disabled",
  allowCampaigns: "disabled",
  allowAgendaCare: "disabled",
  allowOdontoCare: "disabled",
  allowCallcenterUser: "disabled",
  allowCallcenterAdmin: "disabled",
  allowCashierPdv: "disabled",
  allowPdv: "disabled",
  allowErpPhone: "disabled",
  allowViewAllSchedules: "disabled",
  allowManageOtherUsersSchedules: "disabled",
  allowLocation: "disabled",
  allUserChat: "disabled",
  spyTicket: "disabled",
  userClosePendingTicket: "enabled",
  allowConnections: "disabled",
  showDashboard: "disabled",
  allowRealTime: "enabled",
  allowAttendance: "enabled",
  allowEmail: "disabled",
  allowKanban: "enabled",
  allowTags: "enabled",
  allowInternalPortal: "disabled",
  allowTasks: "disabled",
  allowMyWallet: "enabled",
  allowSchedules: "disabled",
  allowBarber: "disabled",
  allowInternalChat: "disabled",
  allowComidaNaPorta: "disabled",
  allowCampaignsList: "disabled",
  allowEmailCampaigns: "disabled",
  allowContactLists: "disabled",
  allowAnnouncements: "disabled",
  allowApi: "disabled",
  allowKnowledgeBase: "disabled",
  allowUsers: "disabled",
  allowUserProfile: "disabled",
  allowQueues: "disabled",
  allowTalkIa: "disabled",
  allowAiAgents: "disabled",
  allowIntegrations: "disabled",
  allowAsaas: "disabled",
  allowWalletManagement: "disabled",
  allowFinanceiro: "disabled",
  allowSettings: "disabled",
};

const UserModal = ({ open, onClose, userId, onImageUpdate }) => {
  const classes = useStyles();
  const toast = useToast();
  const { user: loggedInUser } = useContext(AuthContext);

  const initialState = useMemo(() => ({
    name: "",
    email: "",
    voip: "",
    voipUser: "",
    voipPass: "",
    password: "",
    profile: "user",
    startWork: "00:00",
    endWork: "23:59",
    farewellMessage: "",
    address: "",
    contactMessage: "",
    bio: "",
    allTicket: "disabled",
    allowGroup: false,
    allHistoric: "disabled",
    allUserChat: "disabled",
    userClosePendingTicket: "enabled",
    showDashboard: "disabled",
    allowRealTime: "disabled",
    allowConnections: "disabled",
    spyTicket: "enabled",
    allowSendAudio: "enabled",
    allowSendMedia: "enabled",
    allowViewAllClosedTickets: "disabled",
    allowContacts: "enabled",
    allowAutomations: "enabled",
    allowCampaigns: "enabled",
    allowAgendaCare: "enabled",
    allowOdontoCare: "enabled",
    allowCallcenterUser: "enabled",
    allowCallcenterAdmin: "enabled",
    allowCashierPdv: "enabled",
    allowPdv: "enabled",
    allowErpPhone: "enabled",
    allowViewAllSchedules: "enabled",
    allowManageOtherUsersSchedules: "disabled",
    allowLocation: "disabled",
    allowAttendance: "enabled",
    allowEmail: "enabled",
    allowKanban: "enabled",
    allowTags: "enabled",
    allowInternalPortal: "enabled",
    allowTasks: "enabled",
    allowMyWallet: "enabled",
    allowSchedules: "enabled",
    allowBarber: "enabled",
    allowInternalChat: "enabled",
    allowComidaNaPorta: "enabled",
    allowCampaignsList: "enabled",
    allowEmailCampaigns: "enabled",
    allowContactLists: "enabled",
    allowAnnouncements: "enabled",
    allowApi: "enabled",
    allowKnowledgeBase: "enabled",
    allowUsers: "enabled",
    allowUserProfile: "enabled",
    allowQueues: "enabled",
    allowTalkIa: "enabled",
    allowAiAgents: "enabled",
    aiAdminAssistantAccess: false,
    allowIntegrations: "enabled",
    allowAsaas: "enabled",
    allowWalletManagement: "enabled",
    allowFinanceiro: "enabled",
    allowSettings: "enabled",
    modulePermissionOverrides: {},
    allowAiHelpRequests: false,
    aiHelpPriority: 0,
    aiHelpSpecialties: "",
    aiHelpOnlineOnly: true,
    aiHelpMaxConcurrent: 3,
    profileImage: null,
    phone: "",
    lastName: "",
    cpf: "",
    rg: "",
    ctps: "",
    birthday: "",
    education: "",
    emergencyContacts: [],
  }), []);

  const [user, setUser] = useState(initialState);
  const [selectedQueueIds, setSelectedQueueIds] = useState([]);
  const [whatsappId, setWhatsappId] = useState("");
  const { whatsApps } = useWhatsApps();
  const [tab, setTab] = useState("general");
  const [avatar, setAvatar] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [avatarModalOpen, setAvatarModalOpen] = useState(false);
  const [originalEmail, setOriginalEmail] = useState("");
  const [emailAccounts, setEmailAccounts] = useState([]);
  const [emailFormSaving, setEmailFormSaving] = useState(false);
  const [moduleCatalog, setModuleCatalog] = useState([]);
  const [emailForm, setEmailForm] = useState({
    id: null,
    name: "",
    email: "",
    senderName: "",
    signatureText: "",
    signatureHtml: "",
    imapHost: "",
    imapPort: 993,
    imapSecure: true,
    imapUser: "",
    imapPass: "",
    smtpHost: "",
    smtpPort: 465,
    smtpSecure: true,
    smtpUser: "",
    smtpPass: "",
    smtpReplyTo: "",
    isDefault: false,
    isActive: true
  });

  useEffect(() => {
    if (!open) return;
    setTab("general");
    if (!userId) {
      setUser(initialState);
      setOriginalEmail("");
      setAvatar(null);
      setSelectedQueueIds([]);
      setWhatsappId("");
      return;
    }

    const fetchUser = async () => {
      try {
        const { data } = await api.get(`/users/${userId}`);
        setUser({
          ...data,
          aiAdminAssistantAccess: Boolean(
            data.permissions?.["ai_admin_assistant.access"]
          )
        });
        setOriginalEmail(data.email);
        setAvatar(null);
        const userQueueIds = data.queues?.map((queue) => queue.id);
        setSelectedQueueIds(userQueueIds || []);
        setWhatsappId(data.whatsappId ? data.whatsappId : "");
      } catch (err) {
        toastError(err);
      }
    };

    fetchUser();
  }, [userId, open, initialState]);

  useEffect(() => {
    if (!open || (loggedInUser.profile !== "admin" && loggedInUser.super !== true)) return;
    api.get("/module-permissions/catalog")
      .then(({ data }) => setModuleCatalog(Array.isArray(data) ? data : []))
      .catch(toastError);
  }, [open, loggedInUser.profile, loggedInUser.super]);

  const handleTabChange = (_event, newValue) => {
    if (
      loggedInUser.profile !== "admin" &&
      (newValue === "modules" ||
        newValue === "modulePermissions" ||
        newValue === "permissions" ||
        newValue === "token")
    ) {
      setTab("general");
      return;
    }
    setTab(newValue);
  };

  const handleGenerateApiToken = async () => {
    try {
      const { data } = await api.post(`/users/${userId}/api-token`);
      setUser((prevState) => ({ ...prevState, apiToken: data.apiToken }));
      toast.success("Token gerado com sucesso!");
    } catch (err) {
      toastError(err);
    }
  };

  const resetEmailForm = () => {
    setEmailForm({
      id: null,
      name: "",
      email: "",
      senderName: "",
      signatureText: "",
      signatureHtml: "",
      imapHost: "",
      imapPort: 993,
      imapSecure: true,
      imapUser: "",
      imapPass: "",
      smtpHost: "",
      smtpPort: 465,
      smtpSecure: true,
      smtpUser: "",
      smtpPass: "",
      smtpReplyTo: "",
      isDefault: false,
      isActive: true
    });
  };

  const loadEmailAccounts = useCallback(async () => {
    if (!userId) {
      setEmailAccounts([]);
      return;
    }
    try {
      const { data } = await api.get("/api/email/accounts", {
        params: { userId }
      });
      setEmailAccounts(Array.isArray(data) ? data : []);
    } catch (err) {
      toastError(err);
    }
  }, [userId]);

  useEffect(() => {
    if (open && tab === "email" && userId) {
      loadEmailAccounts();
    }
  }, [open, tab, userId, loadEmailAccounts]);

  const handleEditEmailAccount = (account) => {
    setEmailForm({
      id: account.id,
      name: account.name || "",
      email: account.email || "",
      senderName: account.senderName || "",
      signatureText: account.signatureText || "",
      signatureHtml: account.signatureHtml || "",
      imapHost: account.imapHost || "",
      imapPort: account.imapPort || 993,
      imapSecure: account.imapSecure !== false,
      imapUser: account.imapUser || "",
      imapPass: "",
      smtpHost: account.smtpHost || "",
      smtpPort: account.smtpPort || 465,
      smtpSecure: account.smtpSecure !== false,
      smtpUser: account.smtpUser || "",
      smtpPass: "",
      smtpReplyTo: account.smtpReplyTo || "",
      isDefault: Boolean(account.isDefault),
      isActive: account.isActive !== false
    });
  };

  const handleSaveEmailAccount = async () => {
    if (!userId) {
      toast.error("Salve o usuário primeiro para configurar e-mail.");
      return;
    }

    setEmailFormSaving(true);
    try {
      const payload = {
        userId,
        name: emailForm.name,
        email: emailForm.email,
        senderName: emailForm.senderName,
        signatureText: emailForm.signatureText || null,
        signatureHtml: emailForm.signatureHtml || null,
        imapHost: emailForm.imapHost,
        imapPort: Number(emailForm.imapPort || 993),
        imapSecure: Boolean(emailForm.imapSecure),
        imapUser: emailForm.imapUser,
        smtpHost: emailForm.smtpHost,
        smtpPort: Number(emailForm.smtpPort || 465),
        smtpSecure: Boolean(emailForm.smtpSecure),
        smtpUser: emailForm.smtpUser,
        smtpReplyTo: emailForm.smtpReplyTo || null,
        isDefault: Boolean(emailForm.isDefault),
        isActive: Boolean(emailForm.isActive)
      };

      if (emailForm.imapPass) payload.imapPass = emailForm.imapPass;
      if (emailForm.smtpPass) payload.smtpPass = emailForm.smtpPass;

      if (emailForm.id) {
        await api.put(`/api/email/accounts/${emailForm.id}`, payload);
      } else {
        if (!emailForm.imapPass || !emailForm.smtpPass) {
          toast.error("Informe senha IMAP e SMTP para cadastrar conta.");
          return;
        }
        await api.post("/api/email/accounts", payload);
      }

      toast.success("Conta de e-mail salva.");
      resetEmailForm();
      await loadEmailAccounts();
    } catch (err) {
      toastError(err);
    } finally {
      setEmailFormSaving(false);
    }
  };

  const handleDeleteEmailAccount = async (accountId) => {
    try {
      await api.delete(`/api/email/accounts/${accountId}`);
      toast.success("Conta removida.");
      await loadEmailAccounts();
      if (emailForm.id === accountId) resetEmailForm();
    } catch (err) {
      toastError(err);
    }
  };

  const handleSaveUser = async (values) => {
    setIsSubmitting(true);
    try {
      const userData = {
        ...values,
        permissions: {
          ...(values.permissions || {}),
          "ai_admin_assistant.access": Boolean(values.aiAdminAssistantAccess)
        },
        whatsappId,
        queueIds: selectedQueueIds,
        allowAiHelpRequests: values.allowAiHelpRequests === true || values.allowAiHelpRequests === "true",
        aiHelpOnlineOnly: values.aiHelpOnlineOnly === true || values.aiHelpOnlineOnly === "true",
        aiHelpPriority: Number(values.aiHelpPriority || 0),
        aiHelpMaxConcurrent: Number(values.aiHelpMaxConcurrent || 3),
      };

      if (userId && values.email === originalEmail) delete userData.email;
      if (!userData.password) delete userData.password;
      delete userData.aiAdminAssistantAccess;

      // O backend bloqueia alterações das próprias permissões. Não envie o
      // campo quando ele não foi efetivamente alterado, pois o estado inicial
      // do Formik contém um objeto vazio mesmo sem nenhuma edição.
      if (
        Number(userId) === Number(loggedInUser.id) &&
        sameModulePermissionOverrides(
          values.modulePermissionOverrides,
          user.modulePermissionOverrides
        )
      ) {
        delete userData.modulePermissionOverrides;
      }

      let savedUser;
      if (userId) {
        const { data } = await api.put(`/users/${userId}`, userData);
        savedUser = data;
      } else {
        const { data } = await api.post("/users", userData);
        savedUser = data;
      }

      if (avatar && typeof avatar === "object") {
        const formData = new FormData();
        formData.append("userId", savedUser.id);
        formData.append("typeArch", "user");
        formData.append("profileImage", avatar);

        await api.post(`/users/${savedUser.id}/media-upload`, formData);
        if (userId === loggedInUser.id) {
          localStorage.setItem("profileImage", savedUser.profileImage);
        }
      }

      toast.success(i18n.t("userModal.success"));
      onClose();
    } catch (err) {
      toastError(err);
    }
    setIsSubmitting(false);
  };

  const handleDropAvatar = async (acceptedFile) => {
    try {
      const formData = new FormData();
      formData.append("userId", userId);
      formData.append("typeArch", "user");
      formData.append("profileImage", acceptedFile);

      const { data } = await api.post(`/users/${userId}/media-upload`, formData);
      const imageUrl = `${backendUrl}/public/company${user.companyId}/user/${data.profileImage}`;

      setAvatar(acceptedFile);
      if (onImageUpdate) onImageUpdate(imageUrl);

      if (userId === loggedInUser.id) {
        localStorage.setItem("profileImage", data.profileImage);
        toast.success(i18n.t("userModal.success"));
        setTimeout(() => window.location.reload(), 1000);
      }

      setAvatarModalOpen(false);
      onClose();
    } catch (err) {
      toastError(err);
    }
  };

  const handleDeleteAvatar = async () => {
    try {
      if (userId && user.profileImage) {
        await api.delete(`/users/${userId}/media-upload`);
        if (userId === loggedInUser.id) localStorage.removeItem("profileImage");
        setAvatar(null);
        setUser((prev) => ({ ...prev, profileImage: null }));
        toast.success(i18n.t("userModal.avatarModal.imageDeleted"));
      } else {
        setAvatar(null);
      }
    } catch (err) {
      toastError(err);
    }
  };

  const getAvatarUrl = () => {
    if (avatar && typeof avatar === "object") {
      return URL.createObjectURL(avatar);
    } else if (user.profileImage) {
      return `${backendUrl}/public/company${user.companyId}/user/${user.profileImage}`;
    }
    return null;
  };

  const handleSetAllCompanyModules = useCallback((setFieldValue, enable) => {
    COMPANY_MODULE_FIELDS.forEach((field) => {
      setFieldValue(field.key, field.boolean ? enable : enable ? "enabled" : "disabled", false);
    });
  }, []);

  const handleToggleAllPermissions = useCallback((setFieldValue, enable) => {
    Object.entries(PERMISSION_BULK_VALUES).forEach(([field, values]) => {
      setFieldValue(field, enable ? values.enabled : values.disabled, false);
    });
  }, []);

  const handleApplyAttendantPreset = useCallback((setFieldValue) => {
    Object.entries(ATTENDANT_PERMISSION_PRESET).forEach(([field, value]) => {
      setFieldValue(field, value, false);
    });
  }, []);

  const canAccessRestrictedUserTabs =
    loggedInUser?.super === true || loggedInUser?.profile === "admin";
  const canAccessEmailTab =
    loggedInUser.profile === "admin" ||
    loggedInUser.profile === "super" ||
    loggedInUser.profile === "user";

  return (
    <div className={classes.root}>
      <Formik
        key={userId || "new"}
        initialValues={user}
        enableReinitialize={true}
        validationSchema={UserSchema}
        onSubmit={handleSaveUser}
      >
        {({ values, touched, errors, isValid, setFieldValue, submitForm }) => (
          <>
            <AvatarModal
              open={avatarModalOpen}
              onClose={() => setAvatarModalOpen(false)}
              onSave={handleDropAvatar}
              currentAvatar={getAvatarUrl()}
              onDelete={handleDeleteAvatar}
            />

            <Dialog
              open={open}
              onClose={onClose}
              fullWidth
              maxWidth="lg"
              className={classes.modernDialog}
            >
              <DialogTitle disableTypography style={{ padding: "20px 24px 10px 24px" }}>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Typography variant="h5" style={{ fontWeight: 800 }}>
                    {userId ? `Editar Usuário: ${values.name || ""}` : "Novo Usuário"}
                  </Typography>
                  <IconButton onClick={onClose} size="small">
                    <Close />
                  </IconButton>
                </Box>
              </DialogTitle>

              <DialogContent style={{ padding: "0 24px 20px 24px" }}>
                <Paper className={classes.modernModalHeader} elevation={0}>
                  <Box display="flex" alignItems="center" gap={2}>
                    <div className={classes.avatarContainer}>
                      <Avatar className={classes.avatar} src={getAvatarUrl()}>
                        {!avatar && !user.profileImage && <PersonIcon />}
                      </Avatar>
                      <div className={classes.avatarOverlay}>
                        <Tooltip title={i18n.t("userModal.title.editImage")} arrow>
                          <IconButton
                            size="small"
                            className={classes.editButton}
                            onClick={() => setAvatarModalOpen(true)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </div>
                    </div>
                    <Box>
                      <Typography variant="h6" style={{ fontWeight: 800, color: "#1e293b" }}>
                        {values.name || "Novo Usuário"}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {values.email || "Gerencie acessos, módulos da empresa e conexões."}
                      </Typography>
                    </Box>
                  </Box>
                  <Chip
                    icon={<PersonIcon />}
                    label={profileLabelMap[values.profile] || values.profile || "Usuário"}
                    color="primary"
                    variant="outlined"
                    style={{ fontWeight: 700 }}
                  />
                </Paper>

                {/* Abas do Modal do Usuário */}
                <div className={classes.tabContainer}>
                  <Tabs
                    value={tab}
                    onChange={handleTabChange}
                    indicatorColor="primary"
                    textColor="primary"
                    variant="scrollable"
                    scrollButtons="auto"
                  >
                    <Tab
                      className={classes.tabItem}
                      value="general"
                      icon={<PersonIcon style={{ fontSize: 18 }} />}
                      iconPosition="start"
                      label="Informações Gerais"
                    />
                    {canAccessRestrictedUserTabs && (
                      <Tab
                        className={classes.tabItem}
                        value="modules"
                        icon={<Settings style={{ fontSize: 18 }} />}
                        iconPosition="start"
                        label="Módulos da Empresa"
                      />
                    )}
                    {canAccessRestrictedUserTabs && (
                      <Tab
                        className={classes.tabItem}
                        value="modulePermissions"
                        icon={<Security style={{ fontSize: 18 }} />}
                        iconPosition="start"
                        label="Permissões dos Módulos"
                      />
                    )}
                    <Tab
                      className={classes.tabItem}
                      value="queues"
                      icon={
                        <Badge badgeContent={selectedQueueIds?.length || 0} color="primary" overlap="rectangular">
                          <Layers style={{ fontSize: 18, marginRight: 6 }} />
                        </Badge>
                      }
                      label="Filas & Conexão"
                    />
                    {canAccessRestrictedUserTabs && (
                      <Tab
                        className={classes.tabItem}
                        value="permissions"
                        icon={<Tune style={{ fontSize: 18 }} />}
                        iconPosition="start"
                        label="Atendimento & Permissões"
                      />
                    )}
                    {canAccessEmailTab && (
                      <Tab
                        className={classes.tabItem}
                        value="email"
                        icon={<Mail style={{ fontSize: 18 }} />}
                        iconPosition="start"
                        label="E-mail"
                      />
                    )}
                    {canAccessEmailTab && (
                      <Tab
                        className={classes.tabItem}
                        value="voip"
                        icon={<Phone style={{ fontSize: 18 }} />}
                        iconPosition="start"
                        label="VoIP"
                      />
                    )}
                    <Tab
                      className={classes.tabItem}
                      value="information"
                      icon={<ListAlt style={{ fontSize: 18 }} />}
                      iconPosition="start"
                      label="Informações"
                    />
                    {canAccessRestrictedUserTabs && (
                      <Tab
                        className={classes.tabItem}
                        value="token"
                        icon={<VpnKey style={{ fontSize: 18 }} />}
                        iconPosition="start"
                        label="Token API"
                      />
                    )}
                  </Tabs>
                </div>

                <div className={classes.tabContentContainer}>
                  {/* TAB: Informações Gerais */}
                  {tab === "general" && (
                    <Box display="flex" flexDirection="column" gap={2}>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <Field
                            as={TextField}
                            label={i18n.t("userModal.form.name")}
                            name="name"
                            error={touched.name && Boolean(errors.name)}
                            helperText={touched.name && errors.name}
                            variant="outlined"
                            fullWidth
                            size="small"
                            className={classes.roundedInput}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Field
                            as={TextField}
                            label={i18n.t("userModal.form.email")}
                            name="email"
                            error={touched.email && Boolean(errors.email)}
                            helperText={touched.email && errors.email}
                            variant="outlined"
                            fullWidth
                            size="small"
                            className={classes.roundedInput}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Field
                            as={TextField}
                            label={i18n.t("userModal.form.password")}
                            type="password"
                            name="password"
                            error={touched.password && Boolean(errors.password)}
                            helperText={touched.password && errors.password}
                            variant="outlined"
                            fullWidth
                            size="small"
                            className={classes.roundedInput}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <FormControl variant="outlined" fullWidth size="small" className={classes.roundedSelect}>
                            <InputLabel id="profile-selection-label">
                              {i18n.t("userModal.form.profile")}
                            </InputLabel>
                            <Field
                              as={Select}
                              label={i18n.t("userModal.form.profile")}
                              name="profile"
                              labelId="profile-selection-label"
                              id="profile-selection"
                            >
                              <MenuItem value="admin">Administrador</MenuItem>
                              <MenuItem value="user">Usuário</MenuItem>
                              <MenuItem value="motoqueiro">Motoqueiro</MenuItem>
                            </Field>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Field
                            as={TextField}
                            label={i18n.t("userModal.form.startWork")}
                            name="startWork"
                            type="time"
                            InputLabelProps={{ shrink: true }}
                            inputProps={{ step: 300 }}
                            variant="outlined"
                            fullWidth
                            size="small"
                            className={classes.roundedInput}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Field
                            as={TextField}
                            label={i18n.t("userModal.form.endWork")}
                            name="endWork"
                            type="time"
                            InputLabelProps={{ shrink: true }}
                            inputProps={{ step: 300 }}
                            variant="outlined"
                            fullWidth
                            size="small"
                            className={classes.roundedInput}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <Field
                            as={TextField}
                            label={i18n.t("userModal.form.farewellMessage")}
                            name="farewellMessage"
                            multiline
                            rows={3}
                            variant="outlined"
                            fullWidth
                            className={classes.roundedInput}
                          />
                        </Grid>
                      </Grid>
                    </Box>
                  )}

                  {/* TAB: Módulos da Empresa (NOVO - Habilitar e Desabilitar Módulos Disponíveis da Company) */}
                  {tab === "modules" && canAccessRestrictedUserTabs && (
                    <Box>
                      <div className={classes.quickActionHeader}>
                        <Typography style={{ fontWeight: 700, fontSize: "0.9rem" }}>
                          Habilitar ou Desabilitar Módulos Disponíveis da Empresa para este Usuário
                        </Typography>
                        <Box display="flex" gap={1}>
                          <Button
                            size="small"
                            variant="contained"
                            color="primary"
                            onClick={() => handleSetAllCompanyModules(setFieldValue, true)}
                          >
                            Habilitar Todos os Módulos
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => handleSetAllCompanyModules(setFieldValue, false)}
                          >
                            Desabilitar Todos
                          </Button>
                        </Box>
                      </div>

                      {COMPANY_MODULE_GROUPS.map((group) => {
                        const fields = group.keys
                          .map((key) => COMPANY_MODULE_FIELDS.find((field) => field.key === key))
                          .filter(Boolean);
                        if (!fields.length) return null;

                        return (
                          <Box key={group.title} mb={2.5}>
                            <Typography className={classes.sectionTitle}>
                              {group.icon} {group.title}
                            </Typography>
                            <div className={classes.permissionGrid}>
                              {fields.map((field) => {
                                const currentValue = values?.[field.key];
                                const isEnabled = field.boolean
                                  ? currentValue === true
                                  : String(currentValue || "enabled") === "enabled";

                                return (
                                  <Paper key={field.key} className={classes.permissionCard}>
                                    <Typography className={classes.permissionLabel}>{field.label}</Typography>
                                    <FormControl variant="outlined" size="small" fullWidth className={classes.roundedSelect}>
                                      <Field
                                        as={Select}
                                        name={field.key}
                                        value={isEnabled ? "enabled" : "disabled"}
                                        onChange={(e) =>
                                          setFieldValue(
                                            field.key,
                                            field.boolean
                                              ? e.target.value === "enabled"
                                              : e.target.value,
                                            false
                                          )
                                        }
                                      >
                                        <MenuItem value="enabled">
                                          <Box display="flex" alignItems="center" gap={0.5}>
                                            <CheckCircle style={{ color: "#10b981", fontSize: 16 }} /> Habilitado
                                          </Box>
                                        </MenuItem>
                                        <MenuItem value="disabled">
                                          <Box display="flex" alignItems="center" gap={0.5}>
                                            <Cancel style={{ color: "#ef4444", fontSize: 16 }} /> Desabilitado
                                          </Box>
                                        </MenuItem>
                                      </Field>
                                    </FormControl>
                                  </Paper>
                                );
                              })}
                            </div>
                          </Box>
                        );
                      })}
                    </Box>
                  )}

                  {tab === "modulePermissions" && canAccessRestrictedUserTabs && (
                    <Box>
                      <Typography className={classes.sectionTitle}>
                        <Security fontSize="small" /> Permissões granulares
                      </Typography>
                      <Typography variant="body2" color="textSecondary" style={{ marginBottom: 12 }}>
                        Herdar usa as permissões da equipe; apenas exceções são gravadas neste usuário.
                      </Typography>
                      {Object.entries(
                        moduleCatalog.reduce((modules, permission) => {
                          modules[permission.module] = modules[permission.module] || [];
                          modules[permission.module].push(permission);
                          return modules;
                        }, {})
                      ).map(([module, permissions]) => (
                        <Box key={module} mb={2.5}>
                          <Typography style={{ fontWeight: 800, marginBottom: 8 }}>
                            {permissions[0]?.moduleLabel}
                          </Typography>
                          <div className={classes.permissionGrid}>
                            {permissions.map((permission) => (
                              <Paper key={permission.key} className={classes.permissionCard}>
                                <Typography className={classes.permissionLabel}>{permission.label}</Typography>
                                <Typography variant="caption" color="textSecondary" display="block" style={{ minHeight: 32 }}>
                                  {permission.description}
                                </Typography>
                                <Select
                                  fullWidth
                                  variant="outlined"
                                  value={values.modulePermissionOverrides?.[permission.key] || "inherit"}
                                  onChange={(event) => {
                                    const next = { ...(values.modulePermissionOverrides || {}) };
                                    if (event.target.value === "inherit") delete next[permission.key];
                                    else next[permission.key] = event.target.value;
                                    setFieldValue("modulePermissionOverrides", next, false);
                                  }}
                                >
                                  <MenuItem value="inherit">Herdar da equipe</MenuItem>
                                  <MenuItem value="allow">Permitir</MenuItem>
                                  <MenuItem value="deny">Negar</MenuItem>
                                </Select>
                              </Paper>
                            ))}
                          </div>
                        </Box>
                      ))}
                    </Box>
                  )}

                  {/* TAB: Filas & WhatsApp */}
                  {tab === "queues" && (
                    <Box display="flex" flexDirection="column" gap={2}>
                      <Typography variant="subtitle2" style={{ fontWeight: 800 }}>
                        Filas de Atendimento Vinculadas
                      </Typography>
                      <QueueSelect
                        selectedQueueIds={selectedQueueIds}
                        onChange={(selectedIds) => setSelectedQueueIds(selectedIds)}
                      />

                      <Divider style={{ margin: "16px 0" }} />

                      <Typography variant="subtitle2" style={{ fontWeight: 800 }}>
                        Conexão WhatsApp Padrão do Usuário
                      </Typography>
                      <FormControl variant="outlined" size="small" fullWidth className={classes.roundedSelect}>
                        <InputLabel id="whatsapp-selection-label">
                          Conexão WhatsApp Padrão
                        </InputLabel>
                        <Select
                          labelId="whatsapp-selection-label"
                          id="whatsapp-selection"
                          value={whatsappId}
                          onChange={(e) => setWhatsappId(e.target.value)}
                          label="Conexão WhatsApp Padrão"
                        >
                          <MenuItem value="">Nenhuma Conexão Padrão</MenuItem>
                          {whatsApps.map((wa) => (
                            <MenuItem key={wa.id} value={wa.id}>
                              {wa.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                  )}

                  {/* TAB: Atendimento & Permissões (Operação de Chamados) */}
                  {tab === "permissions" && canAccessRestrictedUserTabs && (
                    <Box>
                      <div className={classes.quickActionHeader}>
                        <Typography style={{ fontWeight: 700, fontSize: "0.9rem" }}>
                          Permissões de Atendimento & Visualização de Tickets
                        </Typography>
                        <Box display="flex" gap={1}>
                          <Button
                            size="small"
                            variant="contained"
                            color="primary"
                            onClick={() => handleApplyAttendantPreset(setFieldValue)}
                          >
                            Preset Atendente
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => handleToggleAllPermissions(setFieldValue, true)}
                          >
                            Habilitar Todas
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => handleToggleAllPermissions(setFieldValue, false)}
                          >
                            Desabilitar Todas
                          </Button>
                        </Box>
                      </div>

                      <div className={classes.permissionGrid}>
                        <Paper className={classes.permissionCard}>
                          <Typography className={classes.permissionLabel}>Ver Chamados sem Fila</Typography>
                          <FormControl variant="outlined" size="small" fullWidth className={classes.roundedSelect}>
                            <Field as={Select} name="allTicket">
                              <MenuItem value="enabled"><Box display="flex" alignItems="center" gap={0.5}><CheckCircle style={{ color: "#10b981", fontSize: 16 }} /> Habilitado</Box></MenuItem>
                              <MenuItem value="disabled"><Box display="flex" alignItems="center" gap={0.5}><Cancel style={{ color: "#ef4444", fontSize: 16 }} /> Desabilitado</Box></MenuItem>
                            </Field>
                          </FormControl>
                        </Paper>

                        <Paper className={classes.permissionCard}>
                          <Typography className={classes.permissionLabel}>Permitir Atendimento em Grupo</Typography>
                          <FormControl variant="outlined" size="small" fullWidth className={classes.roundedSelect}>
                            <Field
                              as={Select}
                              name="allowGroup"
                              value={values.allowGroup ? true : false}
                              onChange={(e) => setFieldValue("allowGroup", e.target.value === "true" || e.target.value === true, false)}
                            >
                              <MenuItem value={true}><Box display="flex" alignItems="center" gap={0.5}><CheckCircle style={{ color: "#10b981", fontSize: 16 }} /> Habilitado</Box></MenuItem>
                              <MenuItem value={false}><Box display="flex" alignItems="center" gap={0.5}><Cancel style={{ color: "#ef4444", fontSize: 16 }} /> Desabilitado</Box></MenuItem>
                            </Field>
                          </FormControl>
                        </Paper>

                        <Paper className={classes.permissionCard}>
                          <Typography className={classes.permissionLabel}>Ver Conversas de Outras Filas</Typography>
                          <FormControl variant="outlined" size="small" fullWidth className={classes.roundedSelect}>
                            <Field as={Select} name="allHistoric">
                              <MenuItem value="enabled"><Box display="flex" alignItems="center" gap={0.5}><CheckCircle style={{ color: "#10b981", fontSize: 16 }} /> Habilitado</Box></MenuItem>
                              <MenuItem value="disabled"><Box display="flex" alignItems="center" gap={0.5}><Cancel style={{ color: "#ef4444", fontSize: 16 }} /> Desabilitado</Box></MenuItem>
                            </Field>
                          </FormControl>
                        </Paper>

                        <Paper className={classes.permissionCard}>
                          <Typography className={classes.permissionLabel}>Ver Conversas de Outros Usuários</Typography>
                          <FormControl variant="outlined" size="small" fullWidth className={classes.roundedSelect}>
                            <Field as={Select} name="allUserChat">
                              <MenuItem value="enabled"><Box display="flex" alignItems="center" gap={0.5}><CheckCircle style={{ color: "#10b981", fontSize: 16 }} /> Habilitado</Box></MenuItem>
                              <MenuItem value="disabled"><Box display="flex" alignItems="center" gap={0.5}><Cancel style={{ color: "#ef4444", fontSize: 16 }} /> Desabilitado</Box></MenuItem>
                            </Field>
                          </FormControl>
                        </Paper>

                        <Paper className={classes.permissionCard}>
                          <Typography className={classes.permissionLabel}>Fechar Tickets Pendentes</Typography>
                          <FormControl variant="outlined" size="small" fullWidth className={classes.roundedSelect}>
                            <Field as={Select} name="userClosePendingTicket">
                              <MenuItem value="enabled"><Box display="flex" alignItems="center" gap={0.5}><CheckCircle style={{ color: "#10b981", fontSize: 16 }} /> Habilitado</Box></MenuItem>
                              <MenuItem value="disabled"><Box display="flex" alignItems="center" gap={0.5}><Cancel style={{ color: "#ef4444", fontSize: 16 }} /> Desabilitado</Box></MenuItem>
                            </Field>
                          </FormControl>
                        </Paper>

                        <Paper className={classes.permissionCard}>
                          <Typography className={classes.permissionLabel}>Ver Tickets Fechados de Todos</Typography>
                          <FormControl variant="outlined" size="small" fullWidth className={classes.roundedSelect}>
                            <Field as={Select} name="allowViewAllClosedTickets">
                              <MenuItem value="enabled"><Box display="flex" alignItems="center" gap={0.5}><CheckCircle style={{ color: "#10b981", fontSize: 16 }} /> Habilitado</Box></MenuItem>
                              <MenuItem value="disabled"><Box display="flex" alignItems="center" gap={0.5}><Cancel style={{ color: "#ef4444", fontSize: 16 }} /> Desabilitado</Box></MenuItem>
                            </Field>
                          </FormControl>
                        </Paper>

                        <Paper className={classes.permissionCard}>
                          <Typography className={classes.permissionLabel}>Espiar Ticket</Typography>
                          <FormControl variant="outlined" size="small" fullWidth className={classes.roundedSelect}>
                            <Field as={Select} name="spyTicket">
                              <MenuItem value="enabled"><Box display="flex" alignItems="center" gap={0.5}><CheckCircle style={{ color: "#10b981", fontSize: 16 }} /> Habilitado</Box></MenuItem>
                              <MenuItem value="disabled"><Box display="flex" alignItems="center" gap={0.5}><Cancel style={{ color: "#ef4444", fontSize: 16 }} /> Desabilitado</Box></MenuItem>
                            </Field>
                          </FormControl>
                        </Paper>

                        <Paper className={classes.permissionCard}>
                          <Typography className={classes.permissionLabel}>Permitir Envio de Mídia</Typography>
                          <FormControl variant="outlined" size="small" fullWidth className={classes.roundedSelect}>
                            <Field as={Select} name="allowSendMedia">
                              <MenuItem value="enabled"><Box display="flex" alignItems="center" gap={0.5}><CheckCircle style={{ color: "#10b981", fontSize: 16 }} /> Habilitado</Box></MenuItem>
                              <MenuItem value="disabled"><Box display="flex" alignItems="center" gap={0.5}><Cancel style={{ color: "#ef4444", fontSize: 16 }} /> Desabilitado</Box></MenuItem>
                            </Field>
                          </FormControl>
                        </Paper>

                        <Paper className={classes.permissionCard}>
                          <Typography className={classes.permissionLabel}>Permitir Envio de Áudio</Typography>
                          <FormControl variant="outlined" size="small" fullWidth className={classes.roundedSelect}>
                            <Field as={Select} name="allowSendAudio">
                              <MenuItem value="enabled"><Box display="flex" alignItems="center" gap={0.5}><CheckCircle style={{ color: "#10b981", fontSize: 16 }} /> Habilitado</Box></MenuItem>
                              <MenuItem value="disabled"><Box display="flex" alignItems="center" gap={0.5}><Cancel style={{ color: "#ef4444", fontSize: 16 }} /> Desabilitado</Box></MenuItem>
                            </Field>
                          </FormControl>
                        </Paper>

                        <Paper className={classes.permissionCard}>
                          <Typography className={classes.permissionLabel}>Ver Painel de Atendimentos</Typography>
                          <FormControl variant="outlined" size="small" fullWidth className={classes.roundedSelect}>
                            <Field as={Select} name="allowRealTime">
                              <MenuItem value="enabled"><Box display="flex" alignItems="center" gap={0.5}><CheckCircle style={{ color: "#10b981", fontSize: 16 }} /> Habilitado</Box></MenuItem>
                              <MenuItem value="disabled"><Box display="flex" alignItems="center" gap={0.5}><Cancel style={{ color: "#ef4444", fontSize: 16 }} /> Desabilitado</Box></MenuItem>
                            </Field>
                          </FormControl>
                        </Paper>

                        <Paper className={classes.permissionCard}>
                          <Typography className={classes.permissionLabel}>Ver Dashboard Geral</Typography>
                          <FormControl variant="outlined" size="small" fullWidth className={classes.roundedSelect}>
                            <Field as={Select} name="showDashboard">
                              <MenuItem value="enabled"><Box display="flex" alignItems="center" gap={0.5}><CheckCircle style={{ color: "#10b981", fontSize: 16 }} /> Habilitado</Box></MenuItem>
                              <MenuItem value="disabled"><Box display="flex" alignItems="center" gap={0.5}><Cancel style={{ color: "#ef4444", fontSize: 16 }} /> Desabilitado</Box></MenuItem>
                            </Field>
                          </FormControl>
                        </Paper>

                        <Paper className={classes.permissionCard}>
                          <Typography className={classes.permissionLabel}>Permitir Localização</Typography>
                          <FormControl variant="outlined" size="small" fullWidth className={classes.roundedSelect}>
                            <Field as={Select} name="allowLocation">
                              <MenuItem value="enabled"><Box display="flex" alignItems="center" gap={0.5}><CheckCircle style={{ color: "#10b981", fontSize: 16 }} /> Habilitado</Box></MenuItem>
                              <MenuItem value="disabled"><Box display="flex" alignItems="center" gap={0.5}><Cancel style={{ color: "#ef4444", fontSize: 16 }} /> Desabilitado</Box></MenuItem>
                            </Field>
                          </FormControl>
                        </Paper>
                      </div>
                    </Box>
                  )}

                  {/* TAB: E-mail */}
                  {tab === "email" && canAccessEmailTab && (
                    <Box display="flex" flexDirection="column" gap={2.5}>
                      <Paper variant="outlined" style={{ padding: 20, borderRadius: 16, border: "1px solid rgba(148,163,184,0.2)" }}>
                        {!userId ? (
                          <Typography variant="body2" color="textSecondary">
                            Salve o usuário primeiro para habilitar a configuração de contas de e-mail (IMAP/SMTP).
                          </Typography>
                        ) : (
                          <Grid container spacing={2.5}>
                            <Grid item xs={12}>
                              <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                                <Mail color="primary" />
                                <Typography variant="subtitle1" style={{ fontWeight: 800 }}>
                                  Configuração de Contas de E-mail (IMAP / SMTP)
                                </Typography>
                              </Box>
                              <Typography variant="body2" color="textSecondary">
                                Cadastre contas de e-mail personalizadas para este usuário enviar e receber e-mails no sistema.
                              </Typography>
                            </Grid>

                            {/* Seção 1: Identificação da Conta */}
                            <Grid item xs={12}>
                              <Paper elevation={0} style={{ padding: 16, borderRadius: 12, border: "1px solid rgba(148,163,184,0.18)", backgroundColor: "rgba(248,250,252,0.6)" }}>
                                <Typography variant="subtitle2" style={{ fontWeight: 700, marginBottom: 12, color: "#1e293b" }}>
                                  1. Identificação da Conta & Remetente
                                </Typography>
                                <Grid container spacing={2}>
                                  <Grid item xs={12} md={6}>
                                    <TextField
                                      label="Nome da Conta (Ex: Suporte, Vendas)"
                                      value={emailForm.name}
                                      onChange={(e) => setEmailForm((prev) => ({ ...prev, name: e.target.value }))}
                                      variant="outlined"
                                      fullWidth
                                      size="small"
                                      className={classes.roundedInput}
                                    />
                                  </Grid>
                                  <Grid item xs={12} md={6}>
                                    <TextField
                                      label="Endereço de E-mail"
                                      value={emailForm.email}
                                      onChange={(e) => setEmailForm((prev) => ({ ...prev, email: e.target.value }))}
                                      variant="outlined"
                                      fullWidth
                                      size="small"
                                      className={classes.roundedInput}
                                    />
                                  </Grid>
                                  <Grid item xs={12} md={6}>
                                    <TextField
                                      label="Nome do Remetente (Ex: João - Empresa)"
                                      value={emailForm.senderName}
                                      onChange={(e) => setEmailForm((prev) => ({ ...prev, senderName: e.target.value }))}
                                      variant="outlined"
                                      fullWidth
                                      size="small"
                                      className={classes.roundedInput}
                                    />
                                  </Grid>
                                  <Grid item xs={12} md={6}>
                                    <TextField
                                      label="Reply-To / Respostas (Opcional)"
                                      value={emailForm.smtpReplyTo}
                                      onChange={(e) => setEmailForm((prev) => ({ ...prev, smtpReplyTo: e.target.value }))}
                                      variant="outlined"
                                      fullWidth
                                      size="small"
                                      className={classes.roundedInput}
                                    />
                                  </Grid>
                                </Grid>
                              </Paper>
                            </Grid>

                            {/* Seção 2: Servidor IMAP */}
                            <Grid item xs={12}>
                              <Paper elevation={0} style={{ padding: 16, borderRadius: 12, border: "1px solid rgba(148,163,184,0.18)", backgroundColor: "rgba(248,250,252,0.6)" }}>
                                <Typography variant="subtitle2" style={{ fontWeight: 700, marginBottom: 12, color: "#1e293b" }}>
                                  2. Servidor de Recebimento (IMAP)
                                </Typography>
                                <Grid container spacing={2}>
                                  <Grid item xs={12} md={6}>
                                    <TextField
                                      label="Host IMAP (Ex: imap.gmail.com)"
                                      value={emailForm.imapHost}
                                      onChange={(e) => setEmailForm((prev) => ({ ...prev, imapHost: e.target.value }))}
                                      variant="outlined"
                                      fullWidth
                                      size="small"
                                      className={classes.roundedInput}
                                    />
                                  </Grid>
                                  <Grid item xs={12} md={2}>
                                    <TextField
                                      label="Porta IMAP"
                                      value={emailForm.imapPort}
                                      onChange={(e) => setEmailForm((prev) => ({ ...prev, imapPort: e.target.value }))}
                                      variant="outlined"
                                      fullWidth
                                      size="small"
                                      className={classes.roundedInput}
                                    />
                                  </Grid>
                                  <Grid item xs={12} md={4}>
                                    <TextField
                                      label="Usuário IMAP"
                                      value={emailForm.imapUser}
                                      onChange={(e) => setEmailForm((prev) => ({ ...prev, imapUser: e.target.value }))}
                                      variant="outlined"
                                      fullWidth
                                      size="small"
                                      className={classes.roundedInput}
                                    />
                                  </Grid>
                                  <Grid item xs={12}>
                                    <TextField
                                      label={emailForm.id ? "Senha IMAP (preencher apenas para alterar)" : "Senha IMAP"}
                                      type="password"
                                      value={emailForm.imapPass}
                                      onChange={(e) => setEmailForm((prev) => ({ ...prev, imapPass: e.target.value }))}
                                      variant="outlined"
                                      fullWidth
                                      size="small"
                                      className={classes.roundedInput}
                                    />
                                  </Grid>
                                </Grid>
                              </Paper>
                            </Grid>

                            {/* Seção 3: Servidor SMTP */}
                            <Grid item xs={12}>
                              <Paper elevation={0} style={{ padding: 16, borderRadius: 12, border: "1px solid rgba(148,163,184,0.18)", backgroundColor: "rgba(248,250,252,0.6)" }}>
                                <Typography variant="subtitle2" style={{ fontWeight: 700, marginBottom: 12, color: "#1e293b" }}>
                                  3. Servidor de Envio (SMTP)
                                </Typography>
                                <Grid container spacing={2}>
                                  <Grid item xs={12} md={6}>
                                    <TextField
                                      label="Host SMTP (Ex: smtp.gmail.com)"
                                      value={emailForm.smtpHost}
                                      onChange={(e) => setEmailForm((prev) => ({ ...prev, smtpHost: e.target.value }))}
                                      variant="outlined"
                                      fullWidth
                                      size="small"
                                      className={classes.roundedInput}
                                    />
                                  </Grid>
                                  <Grid item xs={12} md={2}>
                                    <TextField
                                      label="Porta SMTP"
                                      value={emailForm.smtpPort}
                                      onChange={(e) => setEmailForm((prev) => ({ ...prev, smtpPort: e.target.value }))}
                                      variant="outlined"
                                      fullWidth
                                      size="small"
                                      className={classes.roundedInput}
                                    />
                                  </Grid>
                                  <Grid item xs={12} md={4}>
                                    <TextField
                                      label="Usuário SMTP"
                                      value={emailForm.smtpUser}
                                      onChange={(e) => setEmailForm((prev) => ({ ...prev, smtpUser: e.target.value }))}
                                      variant="outlined"
                                      fullWidth
                                      size="small"
                                      className={classes.roundedInput}
                                    />
                                  </Grid>
                                  <Grid item xs={12}>
                                    <TextField
                                      label={emailForm.id ? "Senha SMTP (preencher apenas para alterar)" : "Senha SMTP"}
                                      type="password"
                                      value={emailForm.smtpPass}
                                      onChange={(e) => setEmailForm((prev) => ({ ...prev, smtpPass: e.target.value }))}
                                      variant="outlined"
                                      fullWidth
                                      size="small"
                                      className={classes.roundedInput}
                                    />
                                  </Grid>
                                </Grid>
                              </Paper>
                            </Grid>

                            {/* Botões de Ação */}
                            <Grid item xs={12}>
                              <Box display="flex" gap={1.5} flexWrap="wrap" mt={1}>
                                <Button
                                  variant="contained"
                                  color="primary"
                                  onClick={handleSaveEmailAccount}
                                  disabled={emailFormSaving}
                                  style={{ fontWeight: 700 }}
                                >
                                  {emailForm.id ? "Atualizar Conta de E-mail" : "Adicionar Conta de E-mail"}
                                </Button>
                                <Button variant="outlined" onClick={resetEmailForm} disabled={emailFormSaving}>
                                  Limpar Formulário
                                </Button>
                              </Box>
                            </Grid>

                            {/* Lista de Contas Cadastradas */}
                            {emailAccounts.length > 0 && (
                              <Grid item xs={12} style={{ marginTop: 8 }}>
                                <Typography variant="subtitle2" style={{ fontWeight: 800, marginBottom: 12, color: "#1e293b" }}>
                                  Contas Cadastradas ({emailAccounts.length})
                                </Typography>
                                <Grid container spacing={1.5}>
                                  {emailAccounts.map((account) => (
                                    <Grid item xs={12} key={account.id}>
                                      <Paper className={classes.emailAccountCard} elevation={0}>
                                        <div className={classes.emailCardHeader}>
                                          <Box>
                                            <Typography style={{ fontWeight: 700, color: "#1e293b" }}>
                                              {account.name} <span style={{ fontWeight: 400, color: "#64748b" }}>({account.email})</span>
                                            </Typography>
                                            <Typography variant="caption" color="textSecondary" style={{ display: "block", marginTop: 2 }}>
                                              IMAP: {account.imapHost}:{account.imapPort} | SMTP: {account.smtpHost}:{account.smtpPort}
                                            </Typography>
                                          </Box>
                                          <Box display="flex" gap={1}>
                                            <Button size="small" variant="outlined" onClick={() => handleEditEmailAccount(account)}>
                                              Editar
                                            </Button>
                                            <Button size="small" color="secondary" onClick={() => handleDeleteEmailAccount(account.id)}>
                                              Excluir
                                            </Button>
                                          </Box>
                                        </div>
                                      </Paper>
                                    </Grid>
                                  ))}
                                </Grid>
                              </Grid>
                            )}
                          </Grid>
                        )}
                      </Paper>
                    </Box>
                  )}

                  {/* TAB: VoIP (Ramal SIP) */}
                  {tab === "voip" && canAccessEmailTab && (
                    <Box display="flex" flexDirection="column" gap={2.5}>
                      <Paper variant="outlined" style={{ padding: 24, borderRadius: 16, border: "1px solid rgba(148,163,184,0.2)" }}>
                        <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                          <Phone color="primary" />
                          <Typography variant="subtitle1" style={{ fontWeight: 800 }}>
                            Configuração de Ramal VoIP / Telefonia SIP
                          </Typography>
                        </Box>
                        <Typography variant="body2" color="textSecondary" style={{ marginBottom: 20 }}>
                          Defina os dados de autenticação SIP para habilitar a telefonia IP e discador direto no sistema.
                        </Typography>

                        <Paper elevation={0} style={{ padding: 20, borderRadius: 14, border: "1px solid rgba(148,163,184,0.18)", backgroundColor: "rgba(248,250,252,0.6)" }}>
                          <Grid container spacing={2.5}>
                            <Grid item xs={12} md={6}>
                              <Field
                                as={TextField}
                                label="Servidor VoIP / Domain SIP (Ex: sip.empresa.com.br)"
                                name="voip"
                                variant="outlined"
                                fullWidth
                                size="small"
                                className={classes.roundedInput}
                              />
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <Field
                                as={TextField}
                                label="Usuário / Ramal SIP (Ex: 1002)"
                                name="voipUser"
                                variant="outlined"
                                fullWidth
                                size="small"
                                className={classes.roundedInput}
                              />
                            </Grid>
                            <Grid item xs={12}>
                              <Field
                                as={TextField}
                                label="Senha do Ramal SIP"
                                type="password"
                                name="voipPass"
                                variant="outlined"
                                fullWidth
                                size="small"
                                className={classes.roundedInput}
                              />
                            </Grid>
                          </Grid>
                        </Paper>
                      </Paper>
                    </Box>
                  )}

                  {/* TAB: Perfil Adicional */}
                  {tab === "information" && (
                    <Box display="flex" flexDirection="column" gap={2}>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <Field
                            as={TextField}
                            label="Nome Completo"
                            name="lastName"
                            variant="outlined"
                            fullWidth
                            size="small"
                            className={classes.roundedInput}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Field
                            as={TextField}
                            label="Telefone / Celular"
                            name="phone"
                            variant="outlined"
                            fullWidth
                            size="small"
                            className={classes.roundedInput}
                          />
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <Field
                            as={TextField}
                            label="CPF"
                            name="cpf"
                            variant="outlined"
                            fullWidth
                            size="small"
                            className={classes.roundedInput}
                          />
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <Field
                            as={TextField}
                            label="RG"
                            name="rg"
                            variant="outlined"
                            fullWidth
                            size="small"
                            className={classes.roundedInput}
                          />
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <Field
                            as={TextField}
                            label="CTPS"
                            name="ctps"
                            variant="outlined"
                            fullWidth
                            size="small"
                            className={classes.roundedInput}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Field
                            as={TextField}
                            label="Data de Nascimento"
                            name="birthday"
                            type="date"
                            InputLabelProps={{ shrink: true }}
                            variant="outlined"
                            fullWidth
                            size="small"
                            className={classes.roundedInput}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Field
                            as={TextField}
                            label="Escolaridade"
                            name="education"
                            variant="outlined"
                            fullWidth
                            size="small"
                            className={classes.roundedInput}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <Field
                            as={TextField}
                            label="Endereço Completo"
                            name="address"
                            variant="outlined"
                            fullWidth
                            size="small"
                            className={classes.roundedInput}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <Field
                            as={TextField}
                            label="Biografia / Descrição Profissional"
                            name="bio"
                            multiline
                            rows={3}
                            variant="outlined"
                            fullWidth
                            className={classes.roundedInput}
                          />
                        </Grid>
                      </Grid>
                    </Box>
                  )}

                  {/* TAB: Token API */}
                  {tab === "token" && canAccessRestrictedUserTabs && (
                    <Box display="flex" flexDirection="column" gap={2}>
                      <Typography variant="subtitle1" style={{ fontWeight: 800 }}>
                        Token de Autenticação da API
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Utilize este token para realizar requisições autenticadas na API externa do sistema em nome deste usuário.
                      </Typography>
                      <Box display="flex" gap={1} alignItems="center" mt={1}>
                        <TextField
                          label="Token de API"
                          value={values.apiToken || "Nenhum token gerado."}
                          variant="outlined"
                          fullWidth
                          size="small"
                          InputProps={{ readOnly: true }}
                          className={classes.roundedInput}
                        />
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={handleGenerateApiToken}
                          style={{ whiteSpace: "nowrap" }}
                        >
                          Gerar Novo Token
                        </Button>
                      </Box>
                    </Box>
                  )}
                </div>
              </DialogContent>

              <DialogActions style={{ padding: "16px 24px", borderTop: "1px solid rgba(148,163,184,0.15)" }}>
                <Button onClick={onClose} color="secondary" disabled={isSubmitting} variant="outlined">
                  {i18n.t("userModal.buttons.cancel")}
                </Button>
                <Button
                  onClick={() => {
                    if (isValid) submitForm();
                  }}
                  color="primary"
                  variant="contained"
                  disabled={isSubmitting}
                  style={{ fontWeight: 700 }}
                >
                  {userId ? i18n.t("userModal.buttons.okEdit") : i18n.t("userModal.buttons.okAdd")}
                </Button>
              </DialogActions>
            </Dialog>
          </>
        )}
      </Formik>
    </div>
  );
};

export default UserModal;
